import { cadinuReferralAbi } from 'config/abi/cadinuReferral'
import { useState } from 'react'
import styled from 'styled-components'
import { formatEther } from 'viem'
import { getCadinuReferralAddress } from 'utils/addressHelpers'
import { useContractReads } from 'wagmi'
import useSWR from 'swr'
import { V3_API_URL } from 'views/TradingCompetitionV3/config'
import { useV3Tx } from 'views/TradingCompetitionV3/components/useV3Tx'
import { V4_ADDRESS } from 'views/TradingCompetitionV4/config'
import { ActionBtn, BigNumber, Hint, Icon, Note, SectionTitle, Surface } from './Identity'
import MyReferralLink from './MyReferralLink'
import { inviterLabel } from './referral'
import { Tag } from './ui'
import { useInviter } from './useReferral'
import { TxState, txLabel } from './chainTx'
import { useOnchainProfile } from './onchainReads'
import { TxStatus } from './onchainUi'

const Tiles = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 14px;
  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`
const Tile = styled.div`
  border-radius: 18px;
  padding: 16px;
  display: grid;
  gap: 10px;
  align-content: start;
  background: rgba(8, 12, 30, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.07);
  .k {
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
`
const Facts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin-top: 12px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSubtle};
  strong {
    color: ${({ theme }) => theme.colors.text};
    font-variant-numeric: tabular-nums;
  }
`

/** The on-chain CADINU profile (CadinuProfile): status and points; on your own profile the actions open in place (Edit drawer, Buy sheet). */
export function OnchainProfileCard({ wallet, mine = true, onManage, onBuy }: { wallet: `0x${string}`; mine?: boolean; onManage?: () => void; onBuy?: () => void }) {
  const { profile: chain } = useOnchainProfile(wallet)
  const registered = chain?.registered
  const active = Boolean(chain?.active)
  return (
    <Surface data-testid="onchain-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <Hint label="the on-chain profile" text="The original CADINU profile on BNB Chain: an NFT locked in the profile contract, points, and the referral discount. Optional; your CADINU profile works without it.">
          <SectionTitle as="h2">On-chain profile</SectionTitle>
        </Hint>
        {registered === undefined ? null : registered ? <Tag $tone={active ? 'ok' : 'off'}>{active ? 'Active' : 'Paused'}</Tag> : <Tag>Not registered</Tag>}
      </div>
      <Tiles>
        <Tile>
          <span className="k">Points</span>
          <BigNumber>{registered === undefined ? '…' : registered && chain ? Number(chain.points).toLocaleString('en-US') : '0'}</BigNumber>
        </Tile>
        {mine && (
          <Tile style={{ justifyContent: 'start' }}>
            <span className="k">{!registered ? 'Not on-chain yet' : active ? 'Locked NFT' : 'No NFT locked (paused)'}</span>
            <div style={{ display: 'grid', gap: 8 }}>
              <ActionBtn type="button" onClick={onManage} $primary={!registered || !active} data-testid="onchain-manage">
                {!registered ? 'Register on-chain' : active ? 'Change NFT or pause' : 'Reactivate'}
              </ActionBtn>
              <ActionBtn type="button" onClick={onBuy} data-testid="onchain-buy-nft">
                Buy a CADINU NFT
              </ActionBtn>
            </div>
          </Tile>
        )}
      </Tiles>
    </Surface>
  )
}

const V4_REF_ABI = [
  { type: 'function', name: 'referralCredit', stateMutability: 'view', outputs: [{ type: 'uint256' }], inputs: [{ type: 'uint8' }, { type: 'address' }] },
  { type: 'function', name: 'referralTotalEarned', stateMutability: 'view', outputs: [{ type: 'uint256' }], inputs: [{ type: 'uint8' }, { type: 'address' }] },
  { type: 'function', name: 'referrerRegistered', stateMutability: 'view', outputs: [{ type: 'bool' }], inputs: [{ type: 'address' }] },
  { type: 'function', name: 'registerReferrer', stateMutability: 'nonpayable', outputs: [], inputs: [] },
  { type: 'function', name: 'withdrawReferralCredit', stateMutability: 'nonpayable', outputs: [], inputs: [{ type: 'uint8', name: 'currency' }] },
] as const
const NFT_REF_ABI = [{ inputs: [], name: 'withdrawReferralReward', outputs: [], stateMutability: 'nonpayable', type: 'function' }] as const

const fmt = (wei: bigint | undefined, digits = 4) =>
  wei === undefined ? '…' : Number(formatEther(wei)).toLocaleString('en-US', { maximumFractionDigits: wei > 10n ** 21n ? 0 : digits })

/**
 * Referrals on the profile (Bez, 2026-10-03): the one link, who invited you, and the earnings per source with the right
 * withdraw button for each: competition creation fees (V4 contract, BNB and CBON, referralCredit / referralTotalEarned) and
 * CADINU NFT sales (CadinuReferral, CBON, referralData). The legacy V3 competition credit shows only when there is some.
 */
export function ReferralCard({
  wallet,
  id,
  signups,
  invitedBy,
}: {
  wallet: `0x${string}`
  id?: string
  signups?: number
  invitedBy?: { address: string; handle: string | null } | null
}) {
  const inv = useInviter(wallet)
  const inviter = invitedBy !== undefined && invitedBy !== null ? invitedBy : inv.bound
  const { send, pending } = useV3Tx()
  const v4 = V4_ADDRESS ? (V4_ADDRESS as `0x${string}`) : undefined
  const { data, refetch } = useContractReads({
    contracts: [
      { address: getCadinuReferralAddress(), abi: cadinuReferralAbi as any, functionName: 'getNumberOfReferredAddress', args: [wallet] },
      { address: getCadinuReferralAddress(), abi: cadinuReferralAbi as any, functionName: 'referralData', args: [wallet] },
      ...(v4
        ? [
            { address: v4, abi: V4_REF_ABI as any, functionName: 'referralCredit', args: [0, wallet] },
            { address: v4, abi: V4_REF_ABI as any, functionName: 'referralCredit', args: [1, wallet] },
            { address: v4, abi: V4_REF_ABI as any, functionName: 'referralTotalEarned', args: [0, wallet] },
            { address: v4, abi: V4_REF_ABI as any, functionName: 'referralTotalEarned', args: [1, wallet] },
            { address: v4, abi: V4_REF_ABI as any, functionName: 'referrerRegistered', args: [wallet] },
          ]
        : []),
    ],
    allowFailure: true,
    watch: false,
  })
  const r = (i: number) => data?.[i]?.result as unknown
  const referred = r(0) as bigint | undefined
  const rd = r(1) as readonly bigint[] | undefined
  const nftAvailable = rd?.[4]
  const nftWithdrawn = rd?.[3]
  const bnbCredit = r(2) as bigint | undefined
  const cbonCredit = r(3) as bigint | undefined
  const bnbTotal = r(4) as bigint | undefined
  const cbonTotal = r(5) as bigint | undefined
  const registered = r(6) as boolean | undefined
  const { data: v3 } = useSWR<{ creditWei?: string }>(`${V3_API_URL}/referral/${wallet}`, (u: string) => fetch(u).then((x) => x.json()), { revalidateOnFocus: false })
  const v3Credit = v3?.creditWei ? BigInt(v3.creditWei) : 0n
  const run = async (label: string, req: Record<string, unknown>) => {
    if (await send(label, req)) refetch()
  }
  // Bez 2026-10-03 (craft): the NFT commission withdraw shows its state inline too; the call itself is unchanged (useV3Tx)
  const [nftTx, setNftTx] = useState<{ state: TxState; hash: string | null; error: string | null }>({ state: 'idle', hash: null, error: null })
  const withdrawNft = async () => {
    setNftTx({ state: 'wallet', hash: null, error: null })
    let err: string | null = null
    const h = await send(
      'Withdraw NFT commission',
      { address: getCadinuReferralAddress(), abi: NFT_REF_ABI, functionName: 'withdrawReferralReward' },
      { onHash: (hash) => setNftTx({ state: 'pending', hash, error: null }), onError: (m) => { err = m } },
    )
    if (h) {
      setNftTx({ state: 'success', hash: h, error: null })
      refetch()
    } else setNftTx((t) => ({ state: 'error', hash: t.hash, error: !err ? 'The withdrawal did not go through.' : /reject|denied|cancel/i.test(err) ? 'Cancelled in the wallet.' : err }))
  }
  const inviterText = inviterLabel(inviter)
  return (
    <Surface $lit data-testid="referral-card">
      <Hint label="your referral link" text="One link for everything. Rewards are paid by the contracts that earn them, and each one is withdrawn here.">
        <SectionTitle as="h2">
          <Icon name="link" size={18} />
          Referrals
        </SectionTitle>
      </Hint>
      {inviterText ? (
        <Note style={{ marginTop: 8 }} data-testid="invited-by" data-state="bound">
          {inviterText}. Set once at your first sign-in; it does not change.
        </Note>
      ) : inv.pending ? (
        <Note style={{ marginTop: 8 }} data-testid="invited-by" data-state="pending">
          {inviterLabel(inv.pending)}. Your first CADINU sign-in binds it to this wallet for good.
        </Note>
      ) : null}
      <div style={{ marginTop: 12 }}>
        <MyReferralLink wallet={wallet} id={id} title="Your referral link" />
      </div>
      <Facts>
        {signups !== undefined && (
          <span>
            <strong>{signups}</strong> joined with your link
          </span>
        )}
        <span>
          <strong>{referred === undefined ? '…' : String(referred)}</strong> NFT buyers referred
        </span>
      </Facts>
      <Tiles>
        <Tile data-testid="ref-src-competition">
          <span className="k">Competitions (creation fees)</span>
          <BigNumber>
            {fmt(bnbCredit, 6)}
            <small>BNB</small>
          </BigNumber>
          <BigNumber>
            {fmt(cbonCredit, 0)}
            <small>CBON</small>
          </BigNumber>
          <Note style={{ margin: 0 }}>
            Earned in total {fmt(bnbTotal, 6)} BNB · {fmt(cbonTotal, 0)} CBON
          </Note>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <ActionBtn type="button" disabled={!v4 || pending || !bnbCredit} onClick={() => run('Withdraw BNB commission', { address: v4, abi: V4_REF_ABI, functionName: 'withdrawReferralCredit', args: [0] })} data-testid="ref-withdraw-bnb">
              Withdraw BNB
            </ActionBtn>
            <ActionBtn type="button" disabled={!v4 || pending || !cbonCredit} onClick={() => run('Withdraw CBON commission', { address: v4, abi: V4_REF_ABI, functionName: 'withdrawReferralCredit', args: [1] })} data-testid="ref-withdraw-cbon">
              Withdraw CBON
            </ActionBtn>
          </div>
          {registered === false && (
            <div data-testid="ref-register" style={{ display: 'grid', gap: 6 }}>
              <Note style={{ margin: 0 }}>Register once (free, one transaction) so people who create a competition with your link pay the referral price and you earn the commission.</Note>
              <ActionBtn type="button" $primary disabled={!v4 || pending} onClick={() => run('Register as referrer', { address: v4, abi: V4_REF_ABI, functionName: 'registerReferrer' })}>
                Register as referrer
              </ActionBtn>
            </div>
          )}
        </Tile>
        <Tile data-testid="ref-src-nft">
          <span className="k">CADINU NFT sales</span>
          <BigNumber>
            {fmt(nftAvailable, 0)}
            <small>CBON</small>
          </BigNumber>
          <Note style={{ margin: 0 }}>Withdrawn so far {fmt(nftWithdrawn, 0)} CBON</Note>
          <ActionBtn type="button" disabled={pending || !nftAvailable} onClick={withdrawNft} data-testid="ref-withdraw-nft">
            {txLabel(nftTx.state === 'success' ? 'idle' : nftTx.state, 'Withdraw CBON')}
          </ActionBtn>
          <TxStatus state={nftTx.state} error={nftTx.error} hash={nftTx.hash} done="Withdrawn to your wallet." testId="ref-withdraw-nft-status" />
        </Tile>
      </Tiles>
      {v3Credit > 0n && (
        <Note style={{ marginTop: 10 }} data-testid="ref-src-legacy">
          Older competitions (previous contract): {fmt(v3Credit, 6)} BNB to withdraw on <a href="/trading-competition/referral" style={{ textDecoration: 'underline' }}>Rewards & referral</a>.
        </Note>
      )}
    </Surface>
  )
}
